import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { authAPI } from '../lib/api';
import { clearStoredAuthSession, getStoredAuthToken, migrateLegacyAuthSession, storeAuthSession } from '../lib/authStorage';

const AuthContext = createContext(null);
const DEFAULT_DOCUMENT_LOCATION_ID_KEY = 'default_document_location_id';
const DEFAULT_DOCUMENT_LOCATION_NAME_KEY = 'default_document_location_name';

const readStoredDocumentLocationValue = (key) => {
  return sessionStorage.getItem(key) || null;
};

const clearStoredDocumentLocation = () => {
  sessionStorage.removeItem(DEFAULT_DOCUMENT_LOCATION_ID_KEY);
  sessionStorage.removeItem(DEFAULT_DOCUMENT_LOCATION_NAME_KEY);
  localStorage.removeItem(DEFAULT_DOCUMENT_LOCATION_ID_KEY);
  localStorage.removeItem(DEFAULT_DOCUMENT_LOCATION_NAME_KEY);
};

const migrateLegacyDocumentLocation = () => {
  const legacyId = localStorage.getItem(DEFAULT_DOCUMENT_LOCATION_ID_KEY);
  const legacyName = localStorage.getItem(DEFAULT_DOCUMENT_LOCATION_NAME_KEY);

  if (legacyId && !sessionStorage.getItem(DEFAULT_DOCUMENT_LOCATION_ID_KEY)) {
    sessionStorage.setItem(DEFAULT_DOCUMENT_LOCATION_ID_KEY, legacyId);
  }

  if (legacyName && !sessionStorage.getItem(DEFAULT_DOCUMENT_LOCATION_NAME_KEY)) {
    sessionStorage.setItem(DEFAULT_DOCUMENT_LOCATION_NAME_KEY, legacyName);
  }

  if (legacyId || legacyName) {
    localStorage.removeItem(DEFAULT_DOCUMENT_LOCATION_ID_KEY);
    localStorage.removeItem(DEFAULT_DOCUMENT_LOCATION_NAME_KEY);
  }
};

const serializeComparableUser = (value) => {
  try {
    return JSON.stringify(value || null);
  } catch {
    return null;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [defaultDocumentLocationId, setDefaultDocumentLocationId] = useState(
    readStoredDocumentLocationValue(DEFAULT_DOCUMENT_LOCATION_ID_KEY)
  );
  const [defaultDocumentLocationName, setDefaultDocumentLocationName] = useState(
    readStoredDocumentLocationValue(DEFAULT_DOCUMENT_LOCATION_NAME_KEY)
  );
  const latestUserRef = useRef(user);

  useEffect(() => {
    latestUserRef.current = user;
  }, [user]);

  const clearStoredSession = useCallback(() => {
    clearStoredAuthSession();
    clearStoredDocumentLocation();
  }, []);

  const setDefaultDocumentLocation = useCallback((location, options = {}) => {
    if (!location?.id) return;

    clearStoredDocumentLocation();
    sessionStorage.setItem(DEFAULT_DOCUMENT_LOCATION_ID_KEY, location.id);
    sessionStorage.setItem(DEFAULT_DOCUMENT_LOCATION_NAME_KEY, location.name || '');

    setDefaultDocumentLocationId(location.id);
    setDefaultDocumentLocationName(location.name || null);
  }, []);

  const syncSessionUser = useCallback(async (fallbackUser = null, { clearOnFailure = false } = {}) => {
    const savedToken = getStoredAuthToken();
    if (!savedToken) return null;

    try {
      const sessionResponse = await authAPI.getSession();
      const sessionUser = sessionResponse.data?.user || fallbackUser;
      if (!sessionUser) return null;
      storeAuthSession({ token: savedToken, user: null });
      setUser((current) => (
        serializeComparableUser(current) === serializeComparableUser(sessionUser)
          ? current
          : sessionUser
      ));

      const savedDocumentLocation = readStoredDocumentLocationValue(DEFAULT_DOCUMENT_LOCATION_ID_KEY);
      if (!savedDocumentLocation && sessionUser?.location_id) {
        setDefaultDocumentLocation({
          id: sessionUser.location_id,
          name: sessionUser.location_name,
        });
      }
      return sessionUser;
    } catch (error) {
      if (clearOnFailure) {
        clearStoredSession();
        setToken(null);
        setUser(null);
        setDefaultDocumentLocationId(null);
        setDefaultDocumentLocationName(null);
      }
      return fallbackUser;
    }
  }, [clearStoredSession]);

  const restoreSession = useCallback(async () => {
    migrateLegacyAuthSession();
    migrateLegacyDocumentLocation();
    const savedToken = getStoredAuthToken();

    if (!savedToken) {
      setLoading(false);
      return;
    }

    try {
      setToken(savedToken);
      await syncSessionUser(null, { clearOnFailure: true });
    } catch {
      clearStoredSession();
      setToken(null);
      setUser(null);
      setDefaultDocumentLocationId(null);
      setDefaultDocumentLocationName(null);
    } finally {
      setLoading(false);
    }
  }, [clearStoredSession, syncSessionUser]);

  useEffect(() => {
    restoreSession();
  }, [restoreSession]);

  useEffect(() => {
    if (!token) return undefined;

    const refreshOnVisibility = () => {
      if (document.visibilityState === 'visible') {
        void authAPI.getSession()
          .then((sessionResponse) => {
            const sessionUser = sessionResponse.data?.user || latestUserRef.current;
            if (!sessionUser) return;
            setUser((current) => (
              serializeComparableUser(current) === serializeComparableUser(sessionUser)
                ? current
                : sessionUser
            ));
          })
          .catch(() => {});
      }
    };
    const refreshOnFocus = () => {
      if (document.visibilityState !== 'visible') return;
      void authAPI.getSession()
        .then((sessionResponse) => {
          const sessionUser = sessionResponse.data?.user || latestUserRef.current;
          if (!sessionUser) return;
          setUser((current) => (
            serializeComparableUser(current) === serializeComparableUser(sessionUser)
              ? current
              : sessionUser
          ));
        })
        .catch(() => {});
    };
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void authAPI.getSession()
          .then((sessionResponse) => {
            const sessionUser = sessionResponse.data?.user || latestUserRef.current;
            if (!sessionUser) return;
            setUser((current) => (
              serializeComparableUser(current) === serializeComparableUser(sessionUser)
                ? current
                : sessionUser
            ));
          })
          .catch(() => {});
      }
    }, 60000);

    document.addEventListener('visibilitychange', refreshOnVisibility);
    window.addEventListener('focus', refreshOnFocus);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', refreshOnVisibility);
      window.removeEventListener('focus', refreshOnFocus);
    };
  }, [token]);

  const login = useCallback(async (newToken, userData) => {
    storeAuthSession({ token: newToken, user: null });
    setToken(newToken);
    setUser(userData);

    const savedToken = getStoredAuthToken();
    if (savedToken) {
      void syncSessionUser(userData, { clearOnFailure: false })
        .then(() => {})
        .catch(() => {
          // keep login payload as fallback
        });
    }
  }, [syncSessionUser]);

  const logout = useCallback(() => {
    clearStoredSession();
    setToken(null);
    setUser(null);
    setDefaultDocumentLocationId(null);
    setDefaultDocumentLocationName(null);
  }, [clearStoredSession]);

  const updateUser = useCallback((patch) => {
    if (!patch || typeof patch !== 'object') return;
    setUser((previousUser) => {
      if (!previousUser) return previousUser;
      return { ...previousUser, ...patch };
    });
  }, []);

  const hasRole = useCallback((roles) => {
    if (!user) return false;
    const compatibleRoles = user.compatible_base_roles || [];
    return roles.includes(user.role) || roles.some((role) => compatibleRoles.includes(role));
  }, [user]);

  const hasPermission = useCallback((permissions, mode = 'some') => {
    if (!user) return false;
    const requiredPermissions = Array.isArray(permissions) ? permissions : [permissions];
    const effectivePermissions = user.effective_permissions || [];

    if (user.role === 'admin' || effectivePermissions.includes('*')) {
      return true;
    }

    if (mode === 'every') {
      return requiredPermissions.every((permission) => effectivePermissions.includes(permission));
    }

    return requiredPermissions.some((permission) => effectivePermissions.includes(permission));
  }, [user]);

  // Getter for token from session storage only (legacy localStorage is migrated once and purged)
  const getToken = useCallback(() => {
    return token || getStoredAuthToken();
  }, [token]);

  const contextValue = useMemo(() => ({
    user,
    token,
    login,
    logout,
    updateUser,
    loading,
    hasRole,
    hasPermission,
    getToken,
    defaultDocumentLocationId,
    defaultDocumentLocationName,
    setDefaultDocumentLocation,
  }), [
    user,
    token,
    login,
    logout,
    updateUser,
    loading,
    hasRole,
    hasPermission,
    getToken,
    defaultDocumentLocationId,
    defaultDocumentLocationName,
    setDefaultDocumentLocation,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
